/**
 * Password Setup Form Component
 * @implements FS-001.2.2 - Password Authentication (Backup)
 * @implements US-008b - Password Setup UI (Frontend)
 *
 * Form for users without a password to set one for the first time.
 * Displays password requirements and validates against complexity rules.
 */

import { useState, FormEvent, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { useSetPasswordMutation } from '@/generated/graphql';
import {
  validatePassword,
  passwordsMatch,
  PASSWORD_REQUIREMENTS,
} from '@/utils';

/**
 * Props for PasswordSetupForm
 */
interface PasswordSetupFormProps {
  /** Callback when password is successfully set */
  onSuccess?: () => void;
}

/**
 * PasswordSetupForm - Form for setting password for the first time
 *
 * Shows when user.hasPassword === false
 *
 * @implements FS-001.2.2 - Password Authentication (Backup)
 */
export const PasswordSetupForm = ({ onSuccess }: PasswordSetupFormProps) => {
  // Form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Mutation
  const [setPassword, { loading }] = useSetPasswordMutation();

  // Validation
  const validation = useMemo(
    () => validatePassword(newPassword),
    [newPassword],
  );
  const passwordsDoMatch = useMemo(
    () => passwordsMatch(newPassword, confirmPassword),
    [newPassword, confirmPassword],
  );

  // Check if form can be submitted
  const canSubmit =
    validation.isValid &&
    passwordsDoMatch &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    !loading;

  /**
   * Handle form submission
   * @implements FS-001.2.2 - Password Authentication (Backup)
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!canSubmit) return;

    try {
      const result = await setPassword({
        variables: {
          input: {
            password: newPassword,
          },
        },
      });

      if (result.data?.setPassword) {
        setSuccess(true);
        setNewPassword('');
        setConfirmPassword('');
        onSuccess?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Set Backup Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set a password as a backup login method in case your OAuth provider is
        temporarily unavailable.
      </Typography>

      {/* Success Message */}
      <Collapse in={success}>
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(false)}
        >
          Password set successfully! You can now use it as a backup login
          method.
        </Alert>
      </Collapse>

      {/* Error Message */}
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>

      {/* New Password Field */}
      <TextField
        label="New Password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        fullWidth
        required
        disabled={loading}
        autoComplete="new-password"
        sx={{ mb: 2 }}
      />

      {/* Confirm Password Field */}
      <TextField
        label="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        fullWidth
        required
        disabled={loading}
        autoComplete="new-password"
        error={confirmPassword.length > 0 && !passwordsDoMatch}
        helperText={
          confirmPassword.length > 0 && !passwordsDoMatch
            ? 'Passwords do not match'
            : ''
        }
        sx={{ mb: 3 }}
      />

      {/* Password Requirements */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Password Requirements:
      </Typography>
      <List dense disablePadding sx={{ mb: 3 }}>
        {PASSWORD_REQUIREMENTS.map((req) => {
          const isMet = req.test(newPassword);
          return (
            <ListItem key={req.code} disablePadding sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isMet ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={req.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: isMet ? 'text.primary' : 'text.secondary',
                }}
              />
            </ListItem>
          );
        })}
      </List>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!canSubmit}
        sx={{ textTransform: 'none' }}
      >
        {loading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Setting Password...
          </>
        ) : (
          'Set Password'
        )}
      </Button>
    </Box>
  );
};
